import { useRef, useImperativeHandle, forwardRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useMap } from "../hooks/useMap";
import type { Alert } from "../data/pipelineData";
import "mapbox-gl/dist/mapbox-gl.css";

interface MapContainerProps {
  themeMode?: "light" | "dark";
}

export interface MapContainerRef {
  flyToAlert: (alert: Alert) => void;
  alerts: Alert[];
  isDroneMode: boolean;
  startDroneAnimation: () => void;
  stopDroneAnimation: () => void;
  resetView: () => void;
  nodesVisible: boolean;
  setNodesVisible: (visible: boolean) => void;
}

export const MapContainer = forwardRef<MapContainerRef, MapContainerProps>(
  ({ themeMode = "dark" }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const {
      isLoaded,
      mapError,
      alerts,
      flyToAlert,
      isDroneMode,
      startDroneAnimation,
      stopDroneAnimation,
      resetView,
      nodesVisible,
      setNodesVisible,
    } = useMap({
      container: containerRef,
      themeMode,
    });

    useImperativeHandle(
      ref,
      () => ({
        flyToAlert,
        alerts,
        isDroneMode,
        startDroneAnimation,
        stopDroneAnimation,
        resetView,
        nodesVisible,
        setNodesVisible,
      }),
      [flyToAlert, alerts, isDroneMode, startDroneAnimation, stopDroneAnimation, resetView, nodesVisible, setNodesVisible]
    );

    if (mapError) {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
          }}
        >
          <Typography color="error">{mapError}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
        <Box
          ref={containerRef}
          sx={{
            height: "100%",
            width: "100%",
            "& .mapboxgl-canvas": {
              outline: "none",
            },
            "& .mapboxgl-ctrl-scale": {
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              color: "text.primary",
              px: 1,
            },
          }}
        />

        {!isLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.default",
              gap: 2,
            }}
          >
            <CircularProgress size={48} thickness={2} />
            <Typography variant="body2" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

MapContainer.displayName = "MapContainer";
